import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  AssociationTransferModalProps,
  TransferItem,
} from './AssociationTransferModal.types';
import { CONFIG_NAMES } from './AssociationTransferModal.types';
import { AssociationTransferModal } from './AssociationTransferModal';

export const AssociationTransferModalContainer = ({
  open,
  sourceConfigName,
  sourceCode,
  sourceName,
  targetConfigName,
  targetItems,
  selectedKeys,
  onTransfer,
  onClose,
}: AssociationTransferModalProps) => {
  const { t } = useTranslation('componentSchema');

  // Draft selection — arrow/drag moves update this locally; API only on Save.
  const [draftKeys, setDraftKeys] = useState<string[]>(selectedKeys);

  useEffect(() => {
    if (open) {
      setDraftKeys(selectedKeys);
    }
  }, [open, selectedKeys]);

  const dataSource: TransferItem[] = useMemo(() => {
    const items = targetItems.map((item) => {
      const isComponentType = targetConfigName === CONFIG_NAMES.COMPONENT_TYPE;
      const title = isComponentType
        ? String(item.name ?? '')
        : `${String(item.code ?? '')} — ${String(item.name ?? '')}`;
      return {
        key: item._key,
        title,
        description: String(item.description ?? item.name ?? ''),
      };
    });

    return items;
  }, [targetItems, targetConfigName]);

  const counterpartLabel = useMemo(() => {
    if (targetConfigName) {
      if (targetConfigName === CONFIG_NAMES.COMPONENT_CLASS) {
        return t('associations.componentClasses');
      }
      if (targetConfigName === CONFIG_NAMES.SUBSYSTEM) {
        return t('associations.subsystems');
      }
      if (targetConfigName === CONFIG_NAMES.COMPONENT_TYPE) {
        return t('associations.componentTypes');
      }
    }

    if (sourceConfigName === CONFIG_NAMES.ASSET_TYPE) {
      return t('associations.subsystems');
    }

    return t('associations.assetTypes');
  }, [targetConfigName, sourceConfigName, t]);

  const handleChange = (newTargetKeys: React.Key[]) => {
    setDraftKeys(newTargetKeys.map(String));
  };

  const handleOk = () => {
    onTransfer(draftKeys);
  };

  const filterOption = (inputValue: string, option: TransferItem) =>
    option.title.toLowerCase().includes(inputValue.toLowerCase()) ||
    option.description.toLowerCase().includes(inputValue.toLowerCase());

  return (
    <AssociationTransferModal
      open={open}
      sourceConfigName={sourceConfigName}
      sourceCode={sourceCode}
      sourceName={sourceName}
      counterpartLabel={counterpartLabel}
      dataSource={dataSource}
      targetKeys={draftKeys}
      onOk={handleOk}
      onClose={onClose}
      onTransferChange={handleChange}
      filterOption={filterOption}
    />
  );
};
